/**
 * ComparisonAnalysisPanel - Displays Financial Comparison results (Ticker A vs Ticker B)
 *
 * Features:
 * - Winner banner with recommended ticker and confidence
 * - Side-by-side anchored market data snapshots
 * - Head-to-head metric comparison table
 * - Relative valuation, moat, catalyst, and risk analysis
 * - Per-ticker thesis display
 * - Kill Sheet for rejected arguments
 */

import { useState } from 'react';
import {
  Scale,
  Clock,
  Database,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  Trophy,
  ArrowRight,
  BarChart3,
  Plus,
  Minus,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TickerLogo } from '@/components/ui/TickerLogo';
import type {
  ComparisonKillSheet,
  HeadToHeadMetric,
  AnchoredSnapshot,
} from './FinancialAnalysisPanel';

interface ComparisonAnalysisPanelProps {
  comparisonKillSheet: ComparisonKillSheet;
  snapshotA?: AnchoredSnapshot;
  snapshotB?: AnchoredSnapshot;
  className?: string;
}

/** Format market cap */
function formatMarketCap(value?: number): string {
  if (!value) return '--';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
}

/** Side-by-side snapshot comparison */
function SnapshotComparison({
  snapshotA,
  snapshotB,
  tickerA,
  tickerB,
}: {
  snapshotA?: AnchoredSnapshot;
  snapshotB?: AnchoredSnapshot;
  tickerA: string;
  tickerB: string;
}) {
  if (!snapshotA && !snapshotB) return null;

  const metrics = [
    {
      label: 'Price',
      a: snapshotA?.priceFormatted || (snapshotA?.price ? `$${snapshotA.price.toFixed(2)}` : '--'),
      b: snapshotB?.priceFormatted || (snapshotB?.price ? `$${snapshotB.price.toFixed(2)}` : '--'),
    },
    {
      label: 'Market Cap',
      a: snapshotA?.marketCapFormatted || formatMarketCap(snapshotA?.marketCap),
      b: snapshotB?.marketCapFormatted || formatMarketCap(snapshotB?.marketCap),
    },
    {
      label: 'P/E (TTM)',
      a: snapshotA?.peFormatted || (snapshotA?.peTTM ? `${snapshotA.peTTM.toFixed(1)}x` : '--'),
      b: snapshotB?.peFormatted || (snapshotB?.peTTM ? `${snapshotB.peTTM.toFixed(1)}x` : '--'),
    },
    {
      label: 'EPS (TTM)',
      a: snapshotA?.epsFormatted || (snapshotA?.epsTTM ? `$${snapshotA.epsTTM.toFixed(2)}` : '--'),
      b: snapshotB?.epsFormatted || (snapshotB?.epsTTM ? `$${snapshotB.epsTTM.toFixed(2)}` : '--'),
    },
    {
      label: 'Change',
      a: snapshotA?.changePercentFormatted || (snapshotA?.changePercent != null ? `${snapshotA.changePercent.toFixed(2)}%` : '--'),
      b: snapshotB?.changePercentFormatted || (snapshotB?.changePercent != null ? `${snapshotB.changePercent.toFixed(2)}%` : '--'),
      highlightA: snapshotA?.changePercent,
      highlightB: snapshotB?.changePercent,
    },
  ];

  return (
    <div className="rounded-xl border border-purple-500/30 bg-gradient-to-b from-purple-500/5 to-transparent overflow-hidden">
      <div className="p-4 border-b border-purple-500/20 bg-purple-500/5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/10">
            <Database className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-purple-400">Side-by-Side Market Data</h3>
            <p className="text-xs text-text-muted">Anchored data for both tickers</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        {/* Header row */}
        <div className="grid grid-cols-3 gap-2 mb-2 text-xs font-mono uppercase tracking-wider">
          <div className="flex items-center justify-end gap-1.5">
            <span className="text-amber-400 font-bold">{tickerA}</span>
            <TickerLogo symbol={tickerA} size="xs" />
          </div>
          <div className="text-center text-text-muted">Metric</div>
          <div className="flex items-center gap-1.5">
            <TickerLogo symbol={tickerB} size="xs" />
            <span className="text-blue-400 font-bold">{tickerB}</span>
          </div>
        </div>
        {/* Metric rows */}
        <div className="space-y-1">
          {metrics.map((m, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 py-1.5 px-2 bg-surface/30 rounded text-sm">
              <div className={cn(
                'text-right font-mono',
                m.highlightA !== undefined
                  ? (m.highlightA ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  : 'text-text-primary'
              )}>
                {m.a}
              </div>
              <div className="text-center text-text-muted text-xs">{m.label}</div>
              <div className={cn(
                'text-left font-mono',
                m.highlightB !== undefined
                  ? (m.highlightB ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  : 'text-text-primary'
              )}>
                {m.b}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Head-to-Head metrics bar chart */
function HeadToHeadTable({
  metrics,
  tickerA,
  tickerB,
}: {
  metrics: HeadToHeadMetric[];
  tickerA: string;
  tickerB: string;
}) {
  if (!metrics || metrics.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-500/30 bg-gradient-to-b from-amber-500/5 to-transparent overflow-hidden">
      <div className="p-4 border-b border-amber-500/20 bg-amber-500/5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <BarChart3 className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-400">Head-to-Head Comparison</h3>
            <p className="text-xs text-text-muted">{metrics.length} metrics compared</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        {/* Column headers */}
        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 mb-3 text-xs font-mono uppercase tracking-wider">
          <div className="flex items-center justify-end gap-1.5">
            <span className="text-green-400 font-bold">{tickerA}</span>
            <TickerLogo symbol={tickerA} size="xs" />
          </div>
          <div className="w-28 text-center text-text-muted">Metric</div>
          <div className="flex items-center gap-1.5">
            <TickerLogo symbol={tickerB} size="xs" />
            <span className="text-blue-400 font-bold">{tickerB}</span>
          </div>
        </div>
        {/* Metric rows */}
        <div className="space-y-2">
          {metrics.map((metric, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center py-2 px-3 rounded-lg bg-surface/50"
            >
              <div className={cn(
                'text-right font-mono text-sm',
                metric.advantage === 'a' ? 'text-green-400 font-bold' : 'text-text-secondary'
              )}>
                {metric.ticker_a_value}
                {metric.advantage === 'a' && <span className="ml-1 text-xs">&#x2713;</span>}
              </div>
              <div className="w-28 text-center text-xs text-text-muted truncate" title={metric.metric_name}>
                {metric.metric_name}
              </div>
              <div className={cn(
                'text-left font-mono text-sm',
                metric.advantage === 'b' ? 'text-blue-400 font-bold' : 'text-text-secondary'
              )}>
                {metric.advantage === 'b' && <span className="mr-1 text-xs">&#x2713;</span>}
                {metric.ticker_b_value}
              </div>
            </div>
          ))}
        </div>
        {/* Score tally */}
        <div className="mt-3 flex items-center justify-between text-xs text-text-muted px-3">
          <span className="text-green-400">
            {tickerA}: {metrics.filter(m => m.advantage === 'a').length} wins
          </span>
          <span>{metrics.filter(m => m.advantage === 'tie').length} ties</span>
          <span className="text-blue-400">
            {tickerB}: {metrics.filter(m => m.advantage === 'b').length} wins
          </span>
        </div>
      </div>
    </div>
  );
}

export function ComparisonAnalysisPanel({
  comparisonKillSheet: ks,
  snapshotA,
  snapshotB,
  className,
}: ComparisonAnalysisPanelProps) {
  const [showDataSources, setShowDataSources] = useState(false);
  const confidencePercent = Math.round(ks.confidence * 100);
  const winner = ks.recommended_ticker;
  const loser = winner === ks.ticker_a ? ks.ticker_b : ks.ticker_a;
  const isAWinner = winner === ks.ticker_a;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="p-5 rounded-xl border border-border/40 bg-gradient-to-r from-amber-500/5 via-transparent to-blue-500/5">
        <div className="flex items-center justify-center gap-4 mb-3">
          {/* Ticker A */}
          <div className="flex items-center gap-2.5">
            <TickerLogo symbol={ks.ticker_a} size="lg" />
            <span className="text-xl font-bold text-amber-400 font-mono">{ks.ticker_a}</span>
          </div>
          {/* VS badge */}
          <div className="flex flex-col items-center">
            <div className="p-2 rounded-full bg-surface-light border border-border">
              <Scale className="w-5 h-5 text-text-secondary" />
            </div>
            <span className="text-[9px] font-bold text-text-muted uppercase tracking-widest mt-1">vs</span>
          </div>
          {/* Ticker B */}
          <div className="flex items-center gap-2.5">
            <span className="text-xl font-bold text-blue-400 font-mono">{ks.ticker_b}</span>
            <TickerLogo symbol={ks.ticker_b} size="lg" />
          </div>
        </div>
        <p className="text-xs text-text-muted text-center">
          {ks.analysis_date} &middot; {ks.research_depth} depth &middot;{' '}
          {ks.total_agents} agents &middot; {ks.debate_rounds} debate round
          {ks.debate_rounds !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Winner Banner */}
      <div className="p-6 rounded-xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-amber-600/5">
        <div className="flex items-center gap-4">
          <div className="relative">
            <TickerLogo symbol={winner} size="lg" className="ring-2 ring-amber-500/50" />
            <Trophy className="w-4 h-4 text-amber-400 absolute -top-1 -right-1 drop-shadow" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-2xl font-bold text-amber-400 font-mono">
                {winner}
              </span>
              <ArrowRight className="w-5 h-5 text-text-muted" />
              <span className="text-lg text-text-muted font-mono line-through decoration-zinc-700">
                {loser}
              </span>
              <span className="text-text-secondary">|</span>
              <span className="text-lg text-text-primary">{confidencePercent}% Confidence</span>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              {ks.comparison_reason}
            </p>
          </div>
        </div>
      </div>

      {/* Side-by-Side Market Data */}
      <SnapshotComparison
        snapshotA={snapshotA}
        snapshotB={snapshotB}
        tickerA={ks.ticker_a}
        tickerB={ks.ticker_b}
      />

      {/* Head-to-Head Metrics */}
      <HeadToHeadTable
        metrics={ks.head_to_head_metrics}
        tickerA={ks.ticker_a}
        tickerB={ks.ticker_b}
      />

      {/* Ticker A vs Ticker B Thesis */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Ticker A Card */}
        <div className={cn(
          'p-4 rounded-xl border',
          'bg-amber-500/5 border-amber-500/20',
          isAWinner && 'ring-1 ring-amber-500/30'
        )}>
          <div className="flex items-center gap-2 mb-3">
            <TickerLogo symbol={ks.ticker_a} size="sm" />
            <h3 className="font-semibold text-amber-400">{ks.ticker_a}</h3>
            {ks.stance_a && (
              <span className={cn(
                'ml-auto px-2 py-0.5 rounded text-[10px] font-mono uppercase',
                ks.stance_a.includes('BULL') ? 'bg-green-500/20 text-green-400'
                  : ks.stance_a.includes('BEAR') ? 'bg-red-500/20 text-red-400'
                  : 'bg-surface-raised text-text-secondary'
              )}>
                {ks.stance_a}
              </span>
            )}
            {isAWinner && !ks.stance_a && <Trophy className="w-4 h-4 text-amber-400 ml-auto" />}
          </div>
          <p className="text-sm text-text-primary leading-relaxed">{ks.ticker_a_thesis}</p>

          {/* Strengths */}
          {ks.ticker_a_strengths && ks.ticker_a_strengths.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-500/10">
              <div className="text-xs text-green-400 font-medium mb-1.5">Key Strengths</div>
              <ul className="space-y-1">
                {ks.ticker_a_strengths.map((s, i) => (
                  <li key={i} className="text-xs text-text-primary flex items-start gap-1.5">
                    <Plus className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Weaknesses */}
          {ks.ticker_a_weaknesses && ks.ticker_a_weaknesses.length > 0 && (
            <div className="mt-2 pt-2 border-t border-amber-500/10">
              <div className="text-xs text-red-400 font-medium mb-1.5">Key Weaknesses</div>
              <ul className="space-y-1">
                {ks.ticker_a_weaknesses.map((w, i) => (
                  <li key={i} className="text-xs text-text-secondary flex items-start gap-1.5">
                    <Minus className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Catalysts */}
          {ks.catalysts_a && ks.catalysts_a.length > 0 && (
            <div className="mt-2 pt-2 border-t border-amber-500/10">
              <div className="text-xs text-amber-400 font-medium mb-1">Catalysts</div>
              <ul className="space-y-1">
                {ks.catalysts_a.map((c, i) => (
                  <li key={i} className="text-xs text-text-secondary flex items-start gap-1">
                    <Zap className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risks */}
          {ks.risks_a && ks.risks_a.length > 0 && (
            <div className="mt-2 pt-2 border-t border-amber-500/10">
              <div className="text-xs text-red-400 font-medium mb-1">Risks</div>
              <ul className="space-y-1">
                {ks.risks_a.map((r, i) => (
                  <li key={i} className="text-xs text-text-secondary flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Ticker B Card */}
        <div className={cn(
          'p-4 rounded-xl border',
          'bg-blue-500/5 border-blue-500/20',
          !isAWinner && 'ring-1 ring-blue-500/30'
        )}>
          <div className="flex items-center gap-2 mb-3">
            <TickerLogo symbol={ks.ticker_b} size="sm" />
            <h3 className="font-semibold text-blue-400">{ks.ticker_b}</h3>
            {ks.stance_b && (
              <span className={cn(
                'ml-auto px-2 py-0.5 rounded text-[10px] font-mono uppercase',
                ks.stance_b.includes('BULL') ? 'bg-green-500/20 text-green-400'
                  : ks.stance_b.includes('BEAR') ? 'bg-red-500/20 text-red-400'
                  : 'bg-surface-raised text-text-secondary'
              )}>
                {ks.stance_b}
              </span>
            )}
            {!isAWinner && !ks.stance_b && <Trophy className="w-4 h-4 text-blue-400 ml-auto" />}
          </div>
          <p className="text-sm text-text-primary leading-relaxed">{ks.ticker_b_thesis}</p>

          {/* Strengths */}
          {ks.ticker_b_strengths && ks.ticker_b_strengths.length > 0 && (
            <div className="mt-3 pt-3 border-t border-blue-500/10">
              <div className="text-xs text-green-400 font-medium mb-1.5">Key Strengths</div>
              <ul className="space-y-1">
                {ks.ticker_b_strengths.map((s, i) => (
                  <li key={i} className="text-xs text-text-primary flex items-start gap-1.5">
                    <Plus className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Weaknesses */}
          {ks.ticker_b_weaknesses && ks.ticker_b_weaknesses.length > 0 && (
            <div className="mt-2 pt-2 border-t border-blue-500/10">
              <div className="text-xs text-red-400 font-medium mb-1.5">Key Weaknesses</div>
              <ul className="space-y-1">
                {ks.ticker_b_weaknesses.map((w, i) => (
                  <li key={i} className="text-xs text-text-primary flex items-start gap-1.5">
                    <Minus className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Catalysts */}
          {ks.catalysts_b && ks.catalysts_b.length > 0 && (
            <div className="mt-2 pt-2 border-t border-blue-500/10">
              <div className="text-xs text-amber-400 font-medium mb-1">Catalysts</div>
              <ul className="space-y-1">
                {ks.catalysts_b.map((c, i) => (
                  <li key={i} className="text-xs text-text-secondary flex items-start gap-1">
                    <Zap className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risks */}
          {ks.risks_b && ks.risks_b.length > 0 && (
            <div className="mt-2 pt-2 border-t border-blue-500/10">
              <div className="text-xs text-red-400 font-medium mb-1">Risks</div>
              <ul className="space-y-1">
                {ks.risks_b.map((r, i) => (
                  <li key={i} className="text-xs text-text-secondary flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Key Differentiators */}
      {ks.key_differentiators && ks.key_differentiators.length > 0 && (
        <div className="p-4 rounded-xl bg-violet-500/5 border border-violet-500/20">
          <h3 className="text-sm font-semibold text-violet-400 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Key Differentiators
          </h3>
          <ol className="space-y-2">
            {ks.key_differentiators.map((diff, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 text-[10px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="text-sm text-text-primary leading-relaxed">{diff}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Decisive Factors */}
      {ks.decisive_factors && ks.decisive_factors.length > 0 && (
        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Decisive Factors
          </h3>
          <ol className="space-y-2">
            {ks.decisive_factors.map((factor, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="text-sm text-text-primary leading-relaxed">{factor}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Relative Analysis Grid */}
      {(ks.relative_valuation || ks.relative_moat || ks.catalyst_comparison || ks.risk_comparison) && (
        <div className="grid md:grid-cols-2 gap-4">
          {ks.relative_valuation && (
            <div className="p-4 rounded-xl bg-surface-light/30 border border-border/30">
              <h4 className="text-sm font-semibold text-amber-400 mb-2">Relative Valuation</h4>
              <p className="text-sm text-text-primary leading-relaxed">{ks.relative_valuation}</p>
            </div>
          )}
          {ks.relative_moat && (
            <div className="p-4 rounded-xl bg-surface-light/30 border border-border/30">
              <h4 className="text-sm font-semibold text-amber-400 mb-2">Competitive Moat</h4>
              <p className="text-sm text-text-primary leading-relaxed">{ks.relative_moat}</p>
            </div>
          )}
          {ks.catalyst_comparison && (
            <div className="p-4 rounded-xl bg-surface-light/30 border border-border/30">
              <h4 className="text-sm font-semibold text-amber-400 mb-2">Catalyst Comparison</h4>
              <p className="text-sm text-text-primary leading-relaxed">{ks.catalyst_comparison}</p>
            </div>
          )}
          {ks.risk_comparison && (
            <div className="p-4 rounded-xl bg-surface-light/30 border border-border/30">
              <h4 className="text-sm font-semibold text-amber-400 mb-2">Risk Comparison</h4>
              <p className="text-sm text-text-primary leading-relaxed">{ks.risk_comparison}</p>
            </div>
          )}
        </div>
      )}

      {/* Caveats */}
      {ks.caveats && (
        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-400 mb-1">Caveats</h3>
              <p className="text-sm text-text-primary leading-relaxed">{ks.caveats}</p>
            </div>
          </div>
        </div>
      )}

      {/* Kill Sheet - Rejected Arguments grouped by ticker */}
      {ks.rejected_arguments.length > 0 && (() => {
        // Group rejected arguments by which ticker's analysis they came from
        // In comparison mode, "bull" side = ticker A agents, "bear" side = ticker B agents
        const tickerAArgs = ks.rejected_arguments.filter(a => a.side === 'bull');
        const tickerBArgs = ks.rejected_arguments.filter(a => a.side === 'bear');
        const otherArgs = ks.rejected_arguments.filter(a => a.side !== 'bull' && a.side !== 'bear');

        return (
          <div className="rounded-xl border-2 border-red-500/30 bg-gradient-to-b from-red-500/5 to-transparent overflow-hidden">
            <div className="p-4 border-b border-red-500/20 bg-red-500/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <Scale className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-400">Kill Sheet</h3>
                    <p className="text-xs text-text-muted">Arguments rejected during comparative debate</p>
                  </div>
                </div>
                <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded-full">
                  {ks.rejected_arguments.length} rejected
                </span>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Ticker A rejected arguments */}
              {tickerAArgs.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className="text-xs font-mono font-bold text-amber-400">{ks.ticker_a}</span>
                    <span className="text-xs text-text-muted">— {tickerAArgs.length} rejected</span>
                  </div>
                  <div className="space-y-2 ml-4">
                    {tickerAArgs.map((arg, idx) => (
                      <div key={idx} className="bg-surface/50 rounded-lg p-3 border border-border text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-amber-400">{arg.agent_role}</span>
                          <span className="text-text-muted">({arg.agent_model})</span>
                        </div>
                        <p className="text-text-primary mb-1">{arg.argument_summary}</p>
                        <p className="text-red-400 text-[10px]">
                          <span className="font-semibold">Rejected:</span> {arg.rejection_reason} — {arg.rejection_detail}
                        </p>
                        {arg.counter_evidence && (
                          <p className="text-text-muted text-[10px] mt-1">Counter: {arg.counter_evidence}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ticker B rejected arguments */}
              {tickerBArgs.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-xs font-mono font-bold text-blue-400">{ks.ticker_b}</span>
                    <span className="text-xs text-text-muted">— {tickerBArgs.length} rejected</span>
                  </div>
                  <div className="space-y-2 ml-4">
                    {tickerBArgs.map((arg, idx) => (
                      <div key={idx} className="bg-surface/50 rounded-lg p-3 border border-border text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-blue-400">{arg.agent_role}</span>
                          <span className="text-text-muted">({arg.agent_model})</span>
                        </div>
                        <p className="text-text-primary mb-1">{arg.argument_summary}</p>
                        <p className="text-red-400 text-[10px]">
                          <span className="font-semibold">Rejected:</span> {arg.rejection_reason} — {arg.rejection_detail}
                        </p>
                        {arg.counter_evidence && (
                          <p className="text-text-muted text-[10px] mt-1">Counter: {arg.counter_evidence}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other rejected arguments (mediator, etc.) */}
              {otherArgs.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-text-muted" />
                    <span className="text-xs font-mono font-bold text-text-secondary">Other</span>
                    <span className="text-xs text-text-muted">— {otherArgs.length} rejected</span>
                  </div>
                  <div className="space-y-2 ml-4">
                    {otherArgs.map((arg, idx) => (
                      <div key={idx} className="bg-surface/50 rounded-lg p-3 border border-border text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-text-secondary">{arg.agent_role}</span>
                          <span className="text-text-muted">({arg.agent_model})</span>
                        </div>
                        <p className="text-text-primary mb-1">{arg.argument_summary}</p>
                        <p className="text-red-400 text-[10px]">
                          <span className="font-semibold">Rejected:</span> {arg.rejection_reason} — {arg.rejection_detail}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Data Sources */}
      {ks.data_sources && Object.keys(ks.data_sources).length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setShowDataSources(!showDataSources)}
            className="w-full flex items-center justify-between p-4 hover:bg-surface-light/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5 text-text-secondary" />
              <span className="text-sm font-medium text-text-primary">Data Sources</span>
              <span className="text-xs text-text-muted">
                ({Object.keys(ks.data_sources).length} sources)
              </span>
            </div>
            {showDataSources ? (
              <ChevronUp className="w-4 h-4 text-text-muted" />
            ) : (
              <ChevronDown className="w-4 h-4 text-text-muted" />
            )}
          </button>
          {showDataSources && (
            <div className="px-4 pb-4 space-y-2">
              {Object.entries(ks.data_sources).map(([source, timestamp]) => (
                <div
                  key={source}
                  className="flex items-center justify-between py-2 px-3 bg-surface/50 rounded-lg"
                >
                  <span className="text-sm text-text-primary uppercase">{source}</span>
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <Clock className="w-3 h-3" />
                    {timestamp}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ComparisonAnalysisPanel;
